/**
 * The plan the user approved, kept reachable while the agent works through it. A port of
 * `bimgo-ai-web`'s `ActivePlanPanel` (owner, 2026-09-10: plan mode mirrors the web).
 *
 * The plan **is the acceptance criteria** for everything that follows, and inside the
 * message list it scrolls away behind the very execution it is meant to be checked
 * against. So it lives outside the transcript, as the web keeps it: a round floating
 * button at the bottom right of the transcript that opens the full plan in a modal.
 *
 * Two rules the first desktop version got wrong, both from the web:
 *
 *   - **Closing is not dismissing.** Closing the modal keeps the button; the plan stays one
 *     click away for the whole turn. Only the small × that appears on hover over the button
 *     removes it, and that is a deliberate "I am done with this plan".
 *   - **A revised plan announces itself.** When the agent re-plans while the modal is
 *     closed, the button gets a "!" badge so the user notices there is something new to read.
 *
 * Positioned by its host: `ThreadsPane` and `SkillEditorChat` render it as the last child
 * of their scrolling transcript, where `.active-plan-fab-wrap` sticks to the bottom edge.
 */
import { useEffect, useRef, useState } from "react";
import { copyText } from "../../lib/clipboard";
import { useLocale } from "../../i18n/LocaleProvider";
import type { ActivePlan } from "../../hooks/useChatTurn";
import { Markdown } from "./Markdown";

/** lucide `ClipboardList` — the web's glyph for a plan. */
function ClipboardListIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </svg>
  );
}

/** lucide `X`. */
function XIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** lucide `Download`. */
function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </svg>
  );
}

/** lucide `Copy`. */
function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

/** lucide `Check`. */
function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Strip characters that are illegal in filenames so a plan title is saveable. */
export function planFilename(title: string): string {
  const cleaned = title.replace(/[\\/:*?"<>|]/g, "").trim().slice(0, 80);
  return `${cleaned || "plan"}.md`;
}

/**
 * Save the plan locally, through exactly one "save as" dialog.
 *
 * In a browser that is Chromium's File System Access picker. In the desktop app it is
 * the plain download, which Electron answers with its own save dialog: the picker opens
 * there too, but the write after it needs the `fileSystem` permission, which the desktop
 * denies like every permission but local network. The write failed, the download
 * fallback ran, and the user was asked where to save a second time (#189).
 *
 * The fallback is for a picker that is missing or fails to open. Once a file has been
 * picked, a failed write is not retried through another dialog. A cancel is not a failure.
 */
async function savePlan(plan: ActivePlan): Promise<void> {
  const filename = planFilename(plan.title);
  const picker = (window as unknown as { showSaveFilePicker?: (opts: unknown) => Promise<FileSystemFileHandle> })
    .showSaveFilePicker;
  if (typeof picker === "function" && !window.bimgo) {
    let handle: FileSystemFileHandle | null = null;
    try {
      handle = await picker({
        suggestedName: filename,
        types: [{ description: "Markdown", accept: { "text/markdown": [".md"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(plan.plan);
      await writable.close();
      return;
    } catch (error) {
      if ((error as DOMException)?.name === "AbortError") return;
      if (handle) {
        console.error("[plan] saving to the picked file failed", error);
        return;
      }
    }
  }
  const blob = new Blob([plan.plan], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ActivePlanPanel({
  plan,
  onDismiss,
}: {
  plan: ActivePlan;
  onDismiss: () => void;
}) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [copied, setCopied] = useState(false);
  const lastPlanRef = useRef<string | null>(null);

  // Flag the button whenever the plan text changes (a new plan or a revision) while the
  // modal is closed, so the user notices there is something new to read.
  useEffect(() => {
    if (plan.plan !== lastPlanRef.current) {
      lastPlanRef.current = plan.plan;
      if (!open) setHasUpdate(true);
    }
  }, [plan.plan, open]);

  // Escape closes the modal — closes, not dismisses.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const title = plan.title.trim() || t("hitl.planLabel");
  const buttonLabel = hasUpdate ? t("activePlan.updated") : t("activePlan.label");

  return (
    <>
      <div className="active-plan-fab-wrap">
        <div className="active-plan-fab-group">
          <button
            type="button"
            className="active-plan-fab"
            title={buttonLabel}
            aria-label={buttonLabel}
            onClick={() => {
              setHasUpdate(false);
              setOpen(true);
            }}
          >
            <ClipboardListIcon />
          </button>
          {hasUpdate ? (
            <span className="active-plan-badge" aria-hidden="true">
              !
            </span>
          ) : null}
          {/* Hover reveals the dismiss control, so the plan can be removed without the
              button taking up space for a close it does not otherwise need. */}
          <button
            type="button"
            className="active-plan-dismiss"
            title={t("activePlan.dismiss")}
            aria-label={t("activePlan.dismiss")}
            onClick={onDismiss}
          >
            <XIcon size={12} />
          </button>
        </div>
      </div>

      {open ? (
        <div className="modal-overlay active-plan-overlay" onClick={() => setOpen(false)}>
          <div
            className="active-plan-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${t("activePlan.label")}: ${title}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="active-plan-modal-head">
              <span className="active-plan-modal-icon" aria-hidden="true">
                <ClipboardListIcon size={16} />
              </span>
              <div className="active-plan-modal-heading">
                <div className="active-plan-modal-label">{t("activePlan.label")}</div>
                <div className="active-plan-modal-title">{title}</div>
              </div>
              <button
                type="button"
                className="active-plan-modal-btn"
                title={t("activePlan.download")}
                aria-label={t("activePlan.download")}
                onClick={() => void savePlan(plan)}
              >
                <DownloadIcon />
              </button>
              <button
                type="button"
                className="active-plan-modal-btn"
                title={copied ? t("activePlan.copied") : t("activePlan.copy")}
                aria-label={copied ? t("activePlan.copied") : t("activePlan.copy")}
                onClick={() => {
                  void copyText(plan.plan).then((ok) => {
                    if (!ok) return;
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  });
                }}
              >
                {copied ? <span className="is-copied"><CheckIcon /></span> : <CopyIcon />}
              </button>
              <button
                type="button"
                className="active-plan-modal-btn is-close"
                title={t("activePlan.close")}
                aria-label={t("activePlan.close")}
                onClick={() => setOpen(false)}
              >
                <XIcon />
              </button>
            </div>
            <div className="active-plan-modal-body">
              <Markdown text={plan.plan} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
